import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/layoutBlack";
import axios from "axios";
import { config } from "../utils/constants";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../components/breadcrumbs";
import PromoOffer from "../components/promoOffer";
import { useRandomScrollPromo } from "../hooks/useRandomScrollPromo";
import { Helmet } from "react-helmet-async";

const BRAND = "#001f3f";

const Shop = () => {
  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;
  const REACT_API_IMAGE_URL = config.url.REACT_API_IMAGE_URL;
  const navigate = useNavigate();
  const CATEGORY_FILTERS = [
  { label: "All", value: "" },
  { label: "Hair Care", value: "Hair Care" },
  { label: "Skin Care", value: "Skin Care" },
  { label: "Wellness", value: "Wellness" },
  // { label: "Wellness", value: "Wellness" },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get("category") || "";
  const setCategoryFilter = (value) => {
  const next = new URLSearchParams(searchParams);
  if (value) next.set("category", value);
  else next.delete("category"); // All
  setSearchParams(next, { replace: false });
  };

  
  const ogImage = "https://molu.co/og/molu-banner-1200x630.png";

  const [families, setFamilies] = useState([]);
    const [promoOpen, setPromoOpen] = useState(false);
  
    // Hook will call this to open randomly
    const onTrigger = useCallback(() => setPromoOpen(true), []);
  
    useRandomScrollPromo({
      onTrigger,
      // Tune these as you like:
      minTimeMs: 1000,           // wait at least 8s on page
      minScrollPx: 300,           // and 800px of scroll
      probability: .8,          // 80% chance when we check
      checkThrottleMs: 1500,      // throttle checks on scroll
      cooldownMs: 3 * 60 * 1000, // 3 min cooldown
      dailyCap: 2,                // once per day
      sessionCap: 1,              // once per session
      storageKeyBase: "promo_random_shop",
    });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
  const params = { page_size: 48 };
  if (selectedCategory) params.category = selectedCategory;

  axios
    .get(`${REACT_APP_API_URL}/products/families/`, { params })
    .then((res) => {
      const data = res.data?.results ?? res.data ?? [];
      setFamilies(Array.isArray(data) ? data : []);
    })
    .catch((err) => {
      console.error("Error fetching product families:", err);
    });
  }, [REACT_APP_API_URL, selectedCategory]);


  const isLocalDevHost = (host) =>
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0|.+\.local|.+\.test)$/i.test(host || "");

  const toHttpsIfNeeded = (u) => {
    try {
      const url = new URL(u);
      const pageIsHttps = typeof window !== "undefined" && window.location.protocol === "https:";
      if (pageIsHttps && url.protocol === "http:" && !isLocalDevHost(url.hostname)) {
        url.protocol = "https:";
      }
      return url.toString();
    } catch {
      return u;
    }
  };

  const joinPath = (base, path) => {
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const asUrl = (path) => {
    if (!path) return "/default.jpg";

    // Absolute URL: maybe upgrade depending on host
    if (/^https?:\/\//i.test(path)) {
      return toHttpsIfNeeded(path);
    }

    // Relative path: build from your image base
    const base = String(REACT_API_IMAGE_URL || "").replace(/\/+$/, "");
    const rel  = String(path).replace(/^\/+/, "");
    return toHttpsIfNeeded(`${base}/${rel}`);
  };
  
  const pickPrimaryImage = (media = []) => {
    const img = media.find((m) => m.media_type === "image" && m.image)?.image;
    if (img) return asUrl(img);
    const poster = media.find((m) => m.poster)?.poster;
    if (poster) return asUrl(poster);
    return "/default.jpg";
  };

  return (
    <div>
      <Layout>
        <Helmet prioritizeSeoTags>
          {/* Basic SEO */}
          <title>Molu | Research-Backed Hair & Skin Care with GHK-Cu + AHK-Cu Peptides</title>
          <meta
            name="description"
            content="Shop Molu for science-driven hair and skincare with GHK-Cu peptides, AHK-Cu peptides, and 2-deoxy-D-ribose (2dDR), featuring our Ionic Peptide Delivery Complex™ in lightweight, modern formulas."
          />
          <meta
            name="keywords"
            content="Molu, GHK-Cu peptides, AHK-Cu peptides, copper peptides, copper peptide serum, copper peptide hair serum, peptide hair care, peptide skin care, 2-deoxy-D-ribose, 2dDR, 2-deoxy-d-ribose serum, ionic peptide delivery complex"
          />
          <link
            rel="canonical"
            href={
              (typeof window !== "undefined" ? window.location.origin : "https://molu.co") +
              "/shop"
            }
          />

          {/* Open Graph */}
          <title>Molu - Wellness Elevated</title>
          <meta key="desc" name="description" content="Molu creates research-backed hair and skincare powered by GHK-Cu peptides, AHK-Cu peptides, and 2-deoxy-D-ribose—enhanced with our Ionic Peptide Delivery Complex™ for lightweight, high-performance formulas." />
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Molu | Science-Driven Hair & Skin Care — GHK-Cu, AHK-Cu + 2-Deoxy-D-Ribose" />
          <meta property="og:description" content="Molu creates research-backed hair and skincare powered by GHK-Cu peptides, AHK-Cu peptides, and 2-deoxy-D-ribose—enhanced with our Ionic Peptide Delivery Complex™ for lightweight, high-performance formulas." />
          <meta property="og:image" content={ogImage} />
          <meta property="og:url" content={(typeof window !== "undefined" ? window.location.href : "")} />
          <meta key="fb:app_id" property="fb:app_id" content="1514691803168501" />          

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Molu | Science-Driven Hair & Skin Care — GHK-Cu, AHK-Cu + 2-Deoxy-D-Ribose" />
          <meta name="twitter:description" content="Molu creates research-backed hair and skincare powered by GHK-Cu peptides, AHK-Cu peptides, and 2-deoxy-D-ribose—enhanced with our Ionic Peptide Delivery Complex™ for lightweight, high-performance formulas." />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
        <div className="container p-t-80">
          <Breadcrumbs />
        </div>

        <section className="full-page-section bg0 p-t-40 p-b-60">
          <div className="container">
            <div className="text-center p-b-20">
              {/* <a
                href="https://www.etsy.com/shop/MoluEssentials"
                className="molu-etsy-btn flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn2 p-lr-15 trans-04 pointer"
                target="_blank"
                rel="noreferrer"
              >
                Visit our Etsy Shop
              </a> */}
            </div>

            <div className="row isotope-grid">
              {/* Category Filters */}
              <div className="d-flex flex-wrap justify-content-center gap-2 p-b-20">
                {CATEGORY_FILTERS.map((f) => {
                  const active = (selectedCategory || "") === (f.value || "");
                  return (
                    <button
                      key={f.label}
                      type="button"
                      className={`btn ${active ? "btn-dark" : "btn-outline-dark"} px-4`}
                      aria-pressed={active}
                      onClick={() => setCategoryFilter(f.value)}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            {families.map((fam) => {
              const defaultId = String(fam.default_variant_id || "");
              const defVar = fam.variants?.[defaultId];
              const imgUrl = pickPrimaryImage(defVar?.media || []);
              const blurb = fam.blurb || "";

              return (
                <div className="col-12 col-sm-6 col-lg-4 p-b-35 isotope-item d-flex box-shadow-below" key={fam.slug || defaultId}>
                  <div
                    className="block2 molu-card h-100 d-flex flex-column hov-img0 w-100"
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/shop/product/${fam.family_slug}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/shop/product/${fam.family_slug}`);
                      }
                    }}
                  >
                    <div className="block2-pic">
                      <img src={imgUrl} alt={fam.name} className="product-img-size" />
                    </div>

                    <div className="block2-txt p-t-14 d-flex flex-column align-items-center text-center flex-grow-1">
                      {/* Uniform text box */}
                      <div className="molu-textbox d-flex flex-column align-items-center text-center w-100">
                        <p className="molu-name fw-bold mb-2">{fam.name}</p>

                        {blurb && (
                          <p className="molu-blurb mb-3 px-2">
                            {blurb}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/shop/product/${fam.family_slug}`);
                        }}
                        className="explore-btn mt-auto mb-3 px-3 py-2 fw-semibold"
                        aria-label={`Explore ${fam.name}`}
                      >
                        Explore
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            </div>

          </div>
          <PromoOffer
            open={promoOpen}
            onOpenChange={setPromoOpen}
            tabText="Get 20% Off"
            imageAlt="New customer discount"
          />
        </section>

        <section className="bg-light py-5 border-top">
          <div className="container text-center">
            <h2 className="fw-bold mb-3">Why Choose Molu?</h2>
            <p className="lead text-muted mb-4">
              Molu is more than just products — it's science-backed wellness for your skin and hair. Our formulas use powerful ingredients like <strong>dual copper peptides (GHK-Cu & AHK-Cu)</strong>, <strong>2-Deoxy-D-Ribose</strong>, and <strong>pterostilbene</strong> to energize and restore at the cellular level.
            </p>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
              <Link to="/start/hair-health-assessment" className="btn btn-warning text-white px-4 py-2 fw-semibold">
                Start Your Hair Assessment
              </Link>
              <Link to="/ten-power-foods" className="btn btn-outline-dark px-4 py-2 fw-semibold">
                Learn About Hair Growth Nutrition
              </Link>
            </div>

            <div className="text-muted mt-4 small">
              ✅ Backed by science & supported by a 90-day money-back guarantee
            </div>
          </div>
        </section>
      </Layout>
    </div>
  );
};

export default Shop;
